import * as XLSX from 'xlsx';
import { GameHistoryItem, PlayerRoundRecord } from '../modules/stats/types';
import { PlayerRole, Faction, VictoryType } from '../types/stats';

export interface ExcelGameRecord {
  updateDate: string;
  gameDate: string | number;
  gameNumber: string;
  playerCount: number;
  dojo: string;
  season: string;
  playerName: string;
  role: string;
  victoryWay: string;
  missionProgress: string;
  isMVP: string;
}

export interface ParseResult {
  games: Partial<GameHistoryItem>[];
  errors: { row: number; errors: string[] }[];
  duplicates: { row: number; reason: string }[];
  stats: {
    totalRows: number;
    validRows: number;
    invalidRows: number;
    duplicateRows: number;
  };
}

export const VICTORY_WAY_MAP: Record<string, { victoryType: VictoryType; winningFaction: Faction; label: string }> = {
  '完成任务梅林隐藏': { victoryType: 'mission', winningFaction: 'good', label: '完成任务梅林隐藏（正义胜）' },
  '提前刺杀失败': { victoryType: 'assassin_fail', winningFaction: 'good', label: '提前刺杀失败（正义胜）' },
  '破坏三次任务': { victoryType: 'sabotage', winningFaction: 'evil', label: '破坏三次任务（邪恶胜）' },
  '刺杀梅林成功': { victoryType: 'assassin_success', winningFaction: 'evil', label: '刺杀梅林成功（邪恶胜）' },
};

function normalizeVictoryWay(victoryWay: string): string {
  return victoryWay
    .replace(/\s+/g, '')
    .replace(/3/g, '三')
    .toLowerCase();
}

function getVictoryInfo(victoryWay: string | undefined): { victoryType: VictoryType; winningFaction: Faction; label: string } | undefined {
  if (!victoryWay) return undefined;
  
  const normalized = normalizeVictoryWay(victoryWay);
  
  const normalizedMap: Record<string, keyof typeof VICTORY_WAY_MAP> = {
    '完成任务梅林隐藏': '完成任务梅林隐藏',
    '提前刺杀失败': '提前刺杀失败',
    '破坏三次任务': '破坏三次任务',
    '刺杀梅林成功': '刺杀梅林成功',
  };
  
  const matchedKey = normalizedMap[normalized];
  if (matchedKey) {
    return VICTORY_WAY_MAP[matchedKey];
  }
  
  return undefined;
}

export const ROLE_NAME_MAP: Record<string, PlayerRole> = {
  '梅林': 'merlin',
  '奥伯伦': 'oberon',
  '莫甘娜': 'morgana',
  '刺客': 'assassin',
  '派西维尔': 'percival',
  '忠臣': 'loyal',
  '莫德雷德': 'mordred',
  '爪牙': 'minion',
};

export const FACTION_MAP: Record<PlayerRole, Faction> = {
  merlin: 'good',
  oberon: 'evil',
  loyal: 'good',
  percival: 'good',
  morgana: 'evil',
  assassin: 'evil',
  mordred: 'evil',
  minion: 'evil',
  lancelot_good: 'good',
  lancelot_evil: 'evil',
};

export function parseExcelFile(file: File, existingGames?: GameHistoryItem[]): Promise<ParseResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target?.result as ArrayBuffer;
        const workbook = XLSX.read(data, { type: 'array' });
        
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
        
        const result = parseGameData(jsonData, existingGames);
        resolve(result);
      } catch (error) {
        reject(new Error(`Excel 解析失败：${error instanceof Error ? error.message : '未知错误'}`));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('文件读取失败'));
    };
    
    reader.readAsArrayBuffer(file);
  });
}

function parseGameData(rows: any[][], existingGames?: GameHistoryItem[]): ParseResult {
  const errors: { row: number; errors: string[] }[] = [];
  const duplicates: { row: number; reason: string }[] = [];
  const gamesMap = new Map<string, { game: Partial<GameHistoryItem>; rowNum: number }>();
  
  const headerRow = rows[9];
  if (!headerRow || headerRow.length < 11) {
    return {
      games: [],
      errors: [{ row: 10, errors: ['未找到有效的表头，请确保第 10 行包含正确的列名'] }],
      duplicates: [],
      stats: { totalRows: 0, validRows: 0, invalidRows: 0, duplicateRows: 0 },
    };
  }
  
  let validCount = 0;
  let invalidCount = 0;
  let duplicateCount = 0;
  
  for (let i = 10; i < rows.length; i++) {
    const row = rows[i];
    
    if (!row || row.length === 0 || row.every((cell: any) => !cell || cell === '')) {
      continue;
    }
    
    const rowErrors: string[] = [];
    
    const record: Partial<ExcelGameRecord> = {
      updateDate: parseDate(row[0]),
      gameDate: parseDate(row[1]),
      gameNumber: row[2]?.toString() || '',
      playerCount: parseInt(row[3]) || 0,
      dojo: row[4]?.toString() || '',
      season: row[5]?.toString() || '',
      playerName: row[6]?.toString() || '',
      role: row[7]?.toString() || '',
      victoryWay: row[8]?.toString() || '',
      missionProgress: row[9]?.toString() || '',
      isMVP: row[10]?.toString() || '',
    };
    
    if (!record.updateDate) rowErrors.push('缺少战绩更新日期');
    if (!record.gameDate) rowErrors.push('缺少对局日期');
    if (!record.gameNumber) rowErrors.push('缺少当日局数');
    if (!record.playerCount || record.playerCount <= 0) rowErrors.push('无效的游戏人数');
    if (!record.dojo) rowErrors.push('缺少道场信息');
    if (!record.season) rowErrors.push('缺少赛季信息');
    if (!record.playerName) rowErrors.push('缺少玩家昵称');
    if (!record.role) rowErrors.push('缺少扮演角色');
    if (!record.victoryWay) rowErrors.push('缺少胜利方式');
    if (!record.missionProgress) rowErrors.push('缺少游戏进程');
    if (!record.isMVP || !['是', '否'].includes(record.isMVP)) rowErrors.push('MVP 字段必须是"是"或"否"');
    
    const victoryInfo = getVictoryInfo(record.victoryWay);
    if (record.victoryWay && !victoryInfo) {
      rowErrors.push(`未知的胜利方式：${record.victoryWay}`);
    }
    
    if (record.role && !ROLE_NAME_MAP[record.role]) {
      rowErrors.push(`未知的角色：${record.role}`);
    }
    
    if (rowErrors.length > 0) {
      errors.push({ row: i + 1, errors: rowErrors });
      invalidCount++;
    } else {
      const gameKey = generateGameKey(
        record.gameDate as string,
        record.gameNumber as string,
        record.dojo as string,
        record.season as string
      );
      
      if (gamesMap.has(gameKey)) {
        const existingEntry = gamesMap.get(gameKey)!;
        const existingGame = existingEntry.game;
        const playerRecord = convertPlayerRecord(record as ExcelGameRecord);
        
        if (playerRecord && existingGame.players) {
          existingGame.players.push(playerRecord as PlayerRoundRecord);
        }
      } else {
        const game = convertToGameRecord(record as ExcelGameRecord, null, null);
        (game as any).dojo_name = record.dojo;
        (game as any).season_name = record.season;
        gamesMap.set(gameKey, { game, rowNum: i + 1 });
        validCount++;
      }
    }
  }
  
  const finalGames: Partial<GameHistoryItem>[] = [];
  const seenKeys = new Set<string>();
  
  for (const [gameKey, { game, rowNum }] of gamesMap.entries()) {
    const [dateStr, gameNumStr, dojoName, seasonName] = gameKey.split('|');
    const gameNum = parseInt(gameNumStr.replace(/[^0-9]/g, '')) || 1;
    
    const isDuplicateInHistory = existingGames?.some(g => {
      if (g.game_date !== dateStr || g.game_number !== gameNum || g.player_count !== game.player_count) {
        return false;
      }
      // 对于道场和赛季，由于历史记录中是 ID，导入数据中是名称
      // 这里我们只比较日期 + 局数 + 人数，避免误判
      // 如果用户需要更严格的去重，可以在 UI 上提供选项
      return true;
    });
    
    if (isDuplicateInHistory) {
      duplicates.push({
        row: rowNum,
        reason: `历史记录中已存在：${dateStr} 第${gameNum}局 ${dojoName} ${seasonName}`,
      });
      duplicateCount++;
      continue;
    }
    
    if (seenKeys.has(gameKey)) {
      duplicates.push({
        row: rowNum,
        reason: `表格内重复：${dateStr} 第${gameNum}局 ${dojoName} ${seasonName}`,
      });
      duplicateCount++;
      continue;
    }
    
    seenKeys.add(gameKey);
    finalGames.push(game);
  }
  
  return {
    games: finalGames,
    errors,
    duplicates,
    stats: {
      totalRows: rows.length - 10,
      validRows: validCount,
      invalidRows: invalidCount,
      duplicateRows: duplicateCount,
    },
  };
}

function convertToGameRecord(record: ExcelGameRecord, dojoId: number | null, seasonId: number | null): Partial<GameHistoryItem> {
  const victoryInfo = getVictoryInfo(record.victoryWay);
  const role = ROLE_NAME_MAP[record.role];
  const faction = role ? FACTION_MAP[role] : undefined;
  
  const scoreChange = calculateScoreChange(
    victoryInfo?.winningFaction || 'good',
    faction || 'good',
    record.isMVP === '是',
    role
  );
  
  const missionResults = parseMissionResults(record.missionProgress);
  
  const playerRecord: Partial<PlayerRoundRecord> = {
    player_nickname: record.playerName,
    player_role: role || 'loyal',
    faction: faction || 'good',
    is_mvp: record.isMVP === '是',
    score_change: scoreChange,
  };
  
  // V195 FIX: MVP 应该存储玩家昵称，而不是固定为 1
  // 实际的玩家编号会在 GameStats.tsx 的 handleExcelImport 中根据 players 数组计算
  
  return {
    game_date: parseDate(record.gameDate) || null,
    game_number: parseInt(record.gameNumber.replace(/[^0-9]/g, '')) || 1,
    player_count: record.playerCount,
    winning_faction: victoryInfo?.winningFaction || 'good',
    victory_type: victoryInfo?.victoryType || 'mission',
    task_results: missionResults,
    players: [playerRecord as PlayerRoundRecord],
    good_mvp_player: null, // 在 handleExcelImport 中根据玩家昵称计算
    evil_mvp_player: null, // 在 handleExcelImport 中根据玩家昵称计算
    dojo_id: dojoId,
    season_id: seasonId,
  };
}

function convertPlayerRecord(record: ExcelGameRecord): Partial<PlayerRoundRecord> | null {
  const role = ROLE_NAME_MAP[record.role];
  const faction = role ? FACTION_MAP[role] : undefined;
  
  const victoryInfo = getVictoryInfo(record.victoryWay);
  
  const scoreChange = calculateScoreChange(
    victoryInfo?.winningFaction || 'good',
    faction || 'good',
    record.isMVP === '是',
    role
  );
  
  return {
    player_nickname: record.playerName,
    player_role: role || 'loyal',
    faction: faction || 'good',
    is_mvp: record.isMVP === '是',
    score_change: scoreChange,
  };
}

function calculateScoreChange(
  winningFaction: Faction,
  playerFaction: Faction,
  isMVP: boolean,
  role?: PlayerRole
): number {
  let baseScore = winningFaction === playerFaction ? 10 : -5;
  
  if (isMVP && winningFaction === playerFaction) {
    baseScore += 5;
  }
  
  if (role === 'merlin' && winningFaction === 'good') {
    baseScore += 2;
  }
  
  return baseScore;
}

function parseMissionResults(missionString: string): Record<string, unknown> {
  const results: Record<string, unknown> = {};
  
  for (let i = 0; i < missionString.length; i++) {
    const char = missionString[i];
    if (char === '蓝') {
      results[`round_${i + 1}`] = true;
    } else if (char === '红') {
      results[`round_${i + 1}`] = false;
    }
  }
  
  return results;
}

function generateGameKey(date: string, gameNumber: string, dojo: string, season: string): string {
  return `${date}|${gameNumber}|${dojo}|${season}`;
}

function parseDate(value: any): string {
  if (!value) return '';
  
  if (typeof value === 'number') {
    const date = new Date(Math.round((value - 25569) * 86400 * 1000));
    return date.toISOString().split('T')[0];
  }
  
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
  }
  
  return '';
}

export function isDuplicateGame(
  date: string,
  roundNumber: number,
  _dojoName: string,
  _seasonName: string,
  existingGames: GameHistoryItem[]
): boolean {
  return existingGames.some(
    (game) =>
      game.game_date === date &&
      game.game_number === roundNumber
  );
}
